import React, { useRef, useState } from 'react';
import './shift.css';

export const TIME_OPTS = [];
for (let t = 8; t <= 24.5 + 1e-9; t += 0.5) TIME_OPTS.push(Math.round(t * 2) / 2);

export const DEFAULT_TIME_BAR = {
  path: '',
  timeKind: 'range',
  rangeFrom: 12,
  rangeTo: 17,
  aroundTime: 21,
  aroundDir: '後',
  hydratedBase: null,
};

export function fmtTime(t) {
  const n = Number(t);
  if (!Number.isFinite(n)) return '';
  const h = Math.floor(n);
  const m = Math.round((n - h) * 60);
  return `${h}:${String(m).padStart(2, '0')}`;
}

export function fmtHour(t) {
  const n = Number(t);
  if (!Number.isFinite(n)) return '';
  if (n === Math.floor(n)) return String(n);
  return fmtTime(n);
}

export function snapTime(t) {
  const n = Number(t);
  if (!Number.isFinite(n)) return 8;
  const clamped = Math.min(24.5, Math.max(8, n));
  return Math.round(clamped * 2) / 2;
}

export function rangeOverrides(from, to) {
  const a = Math.min(snapTime(from), snapTime(to));
  const b = Math.max(snapTime(from), snapTime(to));
  return [
    { time: a, dir: '前', state: '不可' },
    { time: b, dir: '後', state: '不可' },
  ];
}

export function detectRange(ovs) {
  const list = ovs || [];
  if (list.length !== 2) return null;
  const pre = list.find((o) => o.dir === '前' && o.state === '不可');
  const post = list.find((o) => o.dir === '後' && o.state === '不可');
  if (!pre || !post) return null;
  return {
    from: Math.min(Number(pre.time), Number(post.time)),
    to: Math.max(Number(pre.time), Number(post.time)),
  };
}

export function allDayBase(e) {
  if (!e || (e.overrides && e.overrides.length)) return null;
  if (e.base === '可' || e.base === '休') return e.base;
  return null;
}

export function cellLabel(e) {
  const ovs = e?.overrides || [];
  const range = detectRange(ovs);
  if (range) return `${fmtHour(range.from)}–${fmtHour(range.to)}`;
  if (ovs.length === 1 && ovs[0].state === '可' && ovs[0].dir === '後') return `${fmtHour(ovs[0].time)}後`;
  if (ovs.length === 1 && ovs[0].state === '可' && ovs[0].dir === '前') return `${fmtHour(ovs[0].time)}前`;
  if (ovs.length) return '時段';
  return allDayBase(e) || '';
}

export function fillSignature(e) {
  if (!e) return 'empty';
  const ovs = Array.isArray(e.overrides) ? e.overrides : [];
  if (ovs.length) {
    const norm = ovs.map((o) => ({
      time: Number(o.time),
      dir: o.dir === '前' ? '前' : '後',
      state: o.state === '不可' ? '不可' : '可',
    }));
    return `ov:${JSON.stringify(norm)}`;
  }
  if (e.base === '可' || e.base === '休') return `base:${e.base}`;
  return 'empty';
}

export function sameFill(entries) {
  if (!entries.length) return { same: false, sample: null };
  const sigs = entries.map(fillSignature);
  const first = sigs[0];
  if (first === 'empty' || sigs.some((s) => s !== first)) return { same: false, sample: null };
  return { same: true, sample: entries[0] };
}

export function describeFill(e) {
  const ovs = e?.overrides || [];
  const range = detectRange(ovs);
  if (range) {
    return {
      ...DEFAULT_TIME_BAR,
      path: 'custom',
      timeKind: 'range',
      rangeFrom: snapTime(range.from),
      rangeTo: snapTime(range.to),
    };
  }
  if (ovs.length === 1 && ovs[0].state === '可') {
    return {
      ...DEFAULT_TIME_BAR,
      path: 'custom',
      timeKind: 'around',
      aroundTime: snapTime(ovs[0].time),
      aroundDir: ovs[0].dir === '前' ? '前' : '後',
    };
  }
  if (ovs.length) return { ...DEFAULT_TIME_BAR, path: 'custom' };
  if (e?.base === '可' || e?.base === '休') {
    return { ...DEFAULT_TIME_BAR, path: '', hydratedBase: e.base };
  }
  return { ...DEFAULT_TIME_BAR };
}

export function TimeBar({ from, to, onChange, disabled, fillMode = 'range' }) {
  const ref = useRef(null);
  const drag = useRef(null);
  const a = snapTime(from);
  const b = snapTime(to);
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  const interactive = fillMode === 'range' && !disabled;

  const posToTime = (clientX) => {
    const el = ref.current;
    if (!el) return lo;
    const r = el.getBoundingClientRect();
    const p = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    const idx = Math.round(p * (TIME_OPTS.length - 1));
    return TIME_OPTS[idx];
  };

  const emit = (x, y) => {
    const lo2 = Math.min(x, y);
    const hi2 = Math.max(x, y);
    onChange(lo2, hi2 === lo2 ? snapTime(lo2 + 0.5) : hi2);
  };

  const onPointerDown = (e) => {
    if (!interactive) return;
    const t = posToTime(e.clientX);
    const distF = Math.abs(t - lo);
    const distT = Math.abs(t - hi);
    if (distF <= distT && distF <= 0.75) drag.current = { mode: 'from' };
    else if (distT < distF && distT <= 0.75) drag.current = { mode: 'to' };
    else drag.current = { mode: 'range', a: t, b: t };
    if (drag.current.mode === 'range') emit(t, t);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!drag.current || !interactive) return;
    const t = posToTime(e.clientX);
    if (drag.current.mode === 'from') emit(t, hi);
    else if (drag.current.mode === 'to') emit(lo, t);
    else emit(drag.current.a, t);
  };

  const onPointerUp = () => { drag.current = null; };

  let leftPct = 0;
  let widthPct = 100;
  let label = '整天可';
  if (fillMode === 'off') {
    widthPct = 0;
    label = '整天休';
  } else if (fillMode === 'around-after') {
    leftPct = (TIME_OPTS.indexOf(snapTime(from)) / (TIME_OPTS.length - 1)) * 100;
    widthPct = 100 - leftPct;
    label = `${fmtTime(from)}後可`;
  } else if (fillMode === 'around-before') {
    const endPct = (TIME_OPTS.indexOf(snapTime(from)) / (TIME_OPTS.length - 1)) * 100;
    leftPct = 0;
    widthPct = endPct;
    label = `${fmtTime(from)}前可`;
  } else if (fillMode === 'range') {
    leftPct = (TIME_OPTS.indexOf(lo) / (TIME_OPTS.length - 1)) * 100;
    widthPct = Math.max(2, (TIME_OPTS.indexOf(hi) / (TIME_OPTS.length - 1)) * 100 - leftPct);
    label = `${fmtTime(lo)}～${fmtTime(hi)}`;
  }

  return (
    <div
      ref={ref}
      className={`avail-timebar ${fillMode === 'off' ? 'is-off' : ''} ${fillMode === 'full' ? 'is-full' : ''}`}
      role="slider"
      aria-valuemin={8}
      aria-valuemax={24.5}
      aria-disabled={!interactive}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {widthPct > 0 && (
        <div className="avail-timebar-fill" style={{ left: `${leftPct}%`, width: `${widthPct}%` }} />
      )}
      {fillMode === 'range' && (
        <>
          <div className="avail-timebar-handle" style={{ left: `${leftPct}%` }} />
          <div className="avail-timebar-handle" style={{ left: `${leftPct + widthPct}%` }} />
        </>
      )}
      <div className="avail-timebar-label">{label}</div>
    </div>
  );
}

export function TimeFields({
  kind, onKind,
  rangeFrom, rangeTo, onRange,
  aroundTime, aroundDir, onAround,
  disabled,
}) {
  return (
    <div className="avail-time-fields">
      <div className="avail-seg">
        <button type="button" className={`avail-seg-btn ${kind === 'range' ? 'on ok' : ''}`} disabled={disabled} onClick={() => onKind('range')}>區間可</button>
        <button type="button" className={`avail-seg-btn ${kind === 'around' ? 'on ok' : ''}`} disabled={disabled} onClick={() => onKind('around')}>幾點前後可</button>
      </div>
      {kind === 'range' && (
        <>
          <TimeBar from={rangeFrom} to={rangeTo} disabled={disabled} fillMode="range" onChange={onRange} />
          <div className="avail-time-row">
            <select value={rangeFrom} disabled={disabled} onChange={(ev) => onRange(snapTime(ev.target.value), rangeTo)}>
              {TIME_OPTS.map((t) => <option key={`f${t}`} value={t}>{fmtTime(t)}</option>)}
            </select>
            <span>～</span>
            <select value={rangeTo} disabled={disabled} onChange={(ev) => onRange(rangeFrom, snapTime(ev.target.value))}>
              {TIME_OPTS.map((t) => <option key={`t${t}`} value={t}>{fmtTime(t)}</option>)}
            </select>
          </div>
        </>
      )}
      {kind === 'around' && (
        <>
          <TimeBar
            from={aroundTime}
            to={aroundTime}
            fillMode={aroundDir === '前' ? 'around-before' : 'around-after'}
            disabled
            onChange={() => {}}
          />
          <div className="avail-time-row">
            <select value={aroundTime} disabled={disabled} onChange={(ev) => onAround(snapTime(ev.target.value), aroundDir)}>
              {TIME_OPTS.map((t) => <option key={`p${t}`} value={t}>{fmtTime(t)}</option>)}
            </select>
            <button type="button" className={`avail-dir-btn ${aroundDir === '前' ? 'on' : ''}`} disabled={disabled} onClick={() => onAround(aroundTime, '前')}>前可</button>
            <button type="button" className={`avail-dir-btn ${aroundDir === '後' ? 'on' : ''}`} disabled={disabled} onClick={() => onAround(aroundTime, '後')}>後可</button>
          </div>
        </>
      )}
    </div>
  );
}

export function AvailEntryForm({ day, initial, onCommit, onCancel, allowClear = false }) {
  const described = describeFill(initial);
  const [path, setPath] = useState(described.path);
  const [timeKind, setTimeKind] = useState(described.timeKind);
  const [rangeFrom, setRangeFrom] = useState(described.rangeFrom);
  const [rangeTo, setRangeTo] = useState(described.rangeTo);
  const [aroundTime, setAroundTime] = useState(described.aroundTime);
  const [aroundDir, setAroundDir] = useState(described.aroundDir);
  const base = described.hydratedBase;

  const commitBase = (val) => onCommit({ day, base: val, overrides: [] });
  const commitTime = () => {
    const entry = timeKind === 'range'
      ? { day, base: null, overrides: rangeOverrides(rangeFrom, rangeTo) }
      : { day, base: null, overrides: [{ time: snapTime(aroundTime), dir: aroundDir, state: '可' }] };
    onCommit(entry);
  };

  return (
    <>
      {path !== 'custom' ? (
        <div className="avail-seg">
          <button type="button" className={`avail-seg-btn on ok${base === '可' ? ' picked' : ''}`} onClick={() => commitBase('可')}>可</button>
          <button type="button" className={`avail-seg-btn on off${base === '休' ? ' picked' : ''}`} onClick={() => commitBase('休')}>休</button>
          <button type="button" className="avail-seg-btn" onClick={() => setPath('custom')}>自訂</button>
        </div>
      ) : (
        <>
          <button type="button" className="avail-text-btn" style={{ marginTop: 0 }} onClick={() => setPath('')}>
            ← 改回可／休
          </button>
          <TimeFields
            kind={timeKind}
            onKind={setTimeKind}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            onRange={(a, b) => { setRangeFrom(a); setRangeTo(b); }}
            aroundTime={aroundTime}
            aroundDir={aroundDir}
            onAround={(t, d) => { setAroundTime(t); setAroundDir(d); }}
          />
          <button type="button" className="avail-apply-btn" onClick={commitTime}>套用時段</button>
        </>
      )}
      <div className="ss-modal-btns" style={{ marginTop: 8 }}>
        {allowClear && (
          <button type="button" onClick={() => onCommit({ day, base: null, overrides: [] })}>清空此日</button>
        )}
        <button type="button" onClick={onCancel}>取消</button>
      </div>
    </>
  );
}
